import { useState } from "react";
import { Cloud, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

/**
 * CegidSyncButton — Synchronise les ecritures Blitzz vers Cegid.
 *
 * Usage par societe (compta interne) ou par client (panel client).
 *   <CegidSyncButton api={api} societeId="soc_xxx" />
 *   <CegidSyncButton api={api} clientId="cli_xxx" />
 */
export function CegidSyncButton({ api, societeId, clientId, label = "Synchroniser Cegid" }) {
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [opts, setOpts] = useState({ only_drafts: false });

  const testConnection = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await api.post("/comptabilite/cegid/test-connection", {});
      setResult({ ok: true, message: r.data.message + ` (${r.data.token_preview})` });
    } catch (e) {
      setResult({ ok: false, message: e.response?.data?.detail || e.message });
    }
    setTesting(false);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const body = { ...opts };
      if (societeId) body.societe_id = societeId;
      if (clientId) body.client_id = clientId;
      const r = await api.post("/comptabilite/cegid/sync", body);
      toast.success(r.data.message || "Synchronisation reussie");
      setResult({ ok: true, ...r.data });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(msg);
      setResult({ ok: false, message: msg });
    }
    setSyncing(false);
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => { setOpen(true); setResult(null); }}
        className="border-[#10b981]/30 text-[#10b981] hover:bg-[#10b981]/10 font-bold text-xs"
        data-testid="cegid-sync-btn"
      >
        <Cloud className="w-3.5 h-3.5 mr-1.5" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md" data-testid="cegid-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-[#10b981]" /> Synchronisation Cegid
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-xs text-slate-400">
              Pousse les ecritures vers Cegid via l'API Loop. Les ecritures deja synchronisees
              ne sont pas renvoyees. Verifiez d'abord la connexion ci-dessous.
            </p>

            {/* Test connection */}
            <Button
              onClick={testConnection}
              disabled={testing}
              variant="outline"
              size="sm"
              className="border-white/10 text-white hover:bg-white/5 w-full"
              data-testid="cegid-test-connection-btn"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              Tester la connexion
            </Button>

            {/* Options */}
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={opts.only_drafts}
                onChange={e => setOpts({ ...opts, only_drafts: e.target.checked })}
                data-testid="cegid-include-drafts"
              />
              Inclure les brouillons (status=draft)
            </label>

            {/* Result banner */}
            {result && (
              <div
                className={`rounded-lg p-3 text-xs ${result.ok ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-200" : "bg-red-500/10 border border-red-500/20 text-red-200"}`}
                data-testid="cegid-sync-result"
              >
                <div className="flex items-center gap-1.5 font-bold">
                  {result.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  {result.ok ? "Succes" : "Erreur"}
                </div>
                <div className="mt-1 break-words">{result.message}</div>
                {result.import_id && (
                  <div className="mt-1 font-mono text-[10px] opacity-80">import_id: {result.import_id}</div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={sync}
                disabled={syncing}
                className="flex-1 bg-[#10b981] hover:bg-[#0d9c6f] text-white font-bold"
                data-testid="cegid-sync-confirm-btn"
              >
                {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Cloud className="w-4 h-4 mr-2" />}
                Synchroniser
              </Button>
              <Button
                onClick={() => setOpen(false)}
                variant="outline"
                className="border-white/10 text-slate-400 hover:bg-white/5"
              >
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CegidSyncButton;
